import Backbone from 'backbone';
import AirDatepicker from 'air-datepicker';
import 'air-datepicker/air-datepicker.css';
import localeEn from 'air-datepicker/locale/en';
import OneDrawer from '@/modules/module-oneDrawer';
import '../../styles/modules/oneSwatch.scss';
import { BestSellerTypeTitles } from '@/views/best-sellers/constants';
import OneTimesService from '@/views/subscriptions/OneTimesService'; // For swatches color
import RechargeGateway from '@/gateway/Recharge';
import { AccountCountryNames } from '@/pages/account/constants';
import AccountService from '@/pages/account/service';
import ProductsService from '@/service/products-service';
import AddressModalService from '@/pages/account/address-modal-service';
import { getRequest } from '@/service/requests';
import RegimenUtils from '@/utils/regimen-utils';
import AwsCloudGateway from '@/gateway/AwsCloud';
import FreshDeskService from '@/views/fresh-desk/service';
import FlashMessageUtil from '@/utils/flash-message-util';
import {
  fireGTMEvent,
  addTooltipOnHover,
  parseErrorDetailsAsHtml,
  TWENTY_FOUR_HOURS_MS,
  accountGTMEventsMap,
  generateRandomNumber
} from '@/utils';
import useLogger from '@/service/logger-service';

useLogger();

export default Backbone.View.extend({
  initialize: async function (_opts) {
    const self = this;
    console.log('init subscription view !');

    self.fobSettings = JSON.parse(localStorage.getItem('fob-settings'));
    self.url = new URL(window.location.href);
    self.baseUrl = self.url.origin;
    self.accountBody = document.getElementsByClassName('account-body')[0];
    self.mainLoader = document.getElementsByClassName('main-loader')[0];
    self.frequencySelector = document.getElementById(
      'global-frequency-selector'
    );
    self.retentionOfferModal = document.querySelector(
      'modal-component.retention-offer-modal'
    );
    self.alertModal = document.querySelector('alert-modal');

    self.startCancelFlowBtn = document.querySelector('#start-cancel-flow-btn');
    self.retentionOfferBtn = document.querySelector('#retention-offer-btn');

    const isReChargeReady = await self.initReCharge();

    if (!isReChargeReady) {
      return;
    }

    self.addresses = await RechargeGateway.getAddresses();

    await self.initSubscriptions();
    setTimeout(async () => {
      await self.initOneTimes();
      await self.initPaymentUpdate();
      self.initAdjustOrderDateAndFrequency();
      self.initCancelSubscription();
      self.initSuccessModal();
      self.initSkipOrderConfirmationModal();
      self.initAddressModal();
    }, 100);
  },

  events: {},

  fireGTMEventWithUserData: function (eventName, eventData = {}) {
    fireGTMEvent(eventName, {
      ...eventData,
      email: window.marketingUserData.email,
      name: window.marketingUserData.name
    });
  },

  initReCharge: async function () {
    const self = this;
    const accountBody = self.accountBody;

    try {
      self.session = await recharge.auth.loginShopifyAppProxy();
      if (!self.session || self.session.customerId == null) {
        throw new Error(
          `Fail to auth with ReCharge or customer is not exist in ReCharge (no subscription)`
        );
      }
      return true;
    } catch (e) {
      console.error(e);
      accountBody.innerHTML = '';
      accountBody.insertAdjacentHTML(
        'beforeend',
        document.getElementById('empty-subscription-content').innerHTML
      );
      return false;
    }
  },

  initSubscriptions: async function () {
    const self = this;
    const accountBody = self.accountBody;
    accountBody.innerHTML = '';
    self.subscriptions = [];

    try {
      const { subscriptions = [] } =
        await recharge.subscription.listSubscriptions(self.session, {
          sort_by: 'id-asc',
          status: 'active',
          limit: 250
        });

      if (subscriptions.length === 0) {
        throw new Error(`No subscription found under this account`);
      }

      const addressMap = new Map(self.addresses.map((addr) => [addr.id, addr]));
      const reversedSubscriptions = subscriptions.reverse();

      const accountProfiles = {};
      const subscriptionGroups = {};
      const groupedSubscriptions = {};

      const productPromises = [];

      for (const sub of reversedSubscriptions) {
        if (sub.status !== 'active') continue;

        const { ecommerce: pid } = sub.external_product_id || {};
        const { ecommerce: vid } = sub.external_variant_id || {};
        const address_id = sub.address_id;
        const subscriptionAddress = addressMap.get(address_id);

        const props = Object.fromEntries(
          sub.properties.map((p) => [p.name, p.value])
        );
        const {
          __profile_name,
          __image,
          __product_handle,
          __profile_id: raw_profile_id,
          __is_retail
        } = props;

        let is_retail = __is_retail === 'true';
        const handleize_profile_name = __profile_name
          ? self.handleize(__profile_name)
          : 'other';
        const trimed_profile_name =
          __profile_name?.replace(`of `, '') || 'other';
        const __profile_id =
          is_retail && !raw_profile_id
            ? `retail_${generateRandomNumber(5)}`
            : handleize_profile_name === 'of-beauty' && !raw_profile_id
              ? 'best seller'
              : raw_profile_id;

        if (!__profile_id || !handleize_profile_name) {
          console.warn('Missing profile info:', sub);
          continue;
        }

        self.subscriptions.push(sub);

        if (!accountProfiles[handleize_profile_name]) {
          accountProfiles[handleize_profile_name] = {
            subscriptionGroups: []
          };
        }
        if (
          !accountProfiles[handleize_profile_name].subscriptionGroups.includes(
            __profile_id
          )
        ) {
          accountProfiles[handleize_profile_name].subscriptionGroups.push(
            __profile_id
          );
        }

        if (!subscriptionGroups[__profile_id]) {
          subscriptionGroups[__profile_id] = [];
        }
        subscriptionGroups[__profile_id].push(sub.id);

        if (!groupedSubscriptions[__profile_id]) {
          groupedSubscriptions[__profile_id] = [];
        }

        productPromises.push(
          self.getProductInfo(pid, vid).then(({ id, handle, image }) => {
            groupedSubscriptions[__profile_id].push({
              id,
              handle,
              sub,
              imageUrl: image,
              profile_id: __profile_id,
              handleize_profile_name,
              trimed_profile_name,
              subscriptionAddress,
              is_retail
            });
          })
        );
      }

      await Promise.all(productPromises);

      const domFragment = document.createDocumentFragment();

      requestAnimationFrame(() => {
        for (const [profile_id, items] of Object.entries(
          groupedSubscriptions
        )) {
          const { handleize_profile_name, trimed_profile_name } = items[0];

          const isBeauty = Object.values(BestSellerTypeTitles).some((x) =>
            items[0].sub.product_title.toLowerCase().includes(x.toLowerCase())
          );

          const allSubsAreRetail = items.every((item) => item.is_retail);

          const wrapper = document.createElement('div');
          wrapper.id = 'subscription-group';
          wrapper.classList.add('subscription-group');
          const cardsHTML = items
            .map(({ sub, imageUrl, id }) => {
              const swatchHtml = sub.variant_title.includes(`/`)
                ? (() => {
                    const swatchColorCode = `${sub.variant_title
                      .split('/')[1]
                      ?.trim()
                      .toLowerCase()
                      .replace(' ', '-')}
              --${
                sub.__product_handle
                  ? sub.__product_handle
                  : self.handleize(sub.product_title)
              }`;
                    const formatted = swatchColorCode.includes('sos')
                      ? swatchColorCode.substring(
                          0,
                          swatchColorCode.indexOf('sos') + 3
                        )
                      : swatchColorCode;
                    return `<span class=\"swatch-image swatch--${formatted}\"></span>`;
                  })()
                : '';

              const productHandle =
                sub.properties.find((prop) => prop.name === '__product_handle')
                  ?.value || '';
              return `
            <div class="account-card subscription"
              data-product-handle="${productHandle}"
              data-sub-id="${sub.id}"
              data-external-id="${id}"
            >
              <div class="subscription-product-image product-core-block media">
                <img src="${imageUrl}" alt="Image of ${sub.product_title}"/>
              </div>
              <div class="subscription-product-desc product-core-block core">
                <p class="product-name title-font-11">${sub.product_title}</p>
                <div class="product-attribute-container">
                  <p class="product-options body-font-2">${sub.variant_title}${
                    sub.quantity > 1 ? ` x ${sub.quantity}` : ''
                  }</p>
                  ${swatchHtml}
                </div>
              </div>
            </div>`;
            })
            .join('');

          wrapper.innerHTML = `
          <h3 class="subscription-profile-index title-font-8"><span>${
            allSubsAreRetail
              ? ''
              : isBeauty
                ? 'Best Sellers'
                : trimed_profile_name
          }${
            trimed_profile_name === 'other' || isBeauty ? '' : "'s"
          }</span> Subscriptions</h3>
          <div class="subscription-profile-list" id="${handleize_profile_name}">
            <div class="profile-wrapper" data-address-id="${
              items[0].sub.address_id
            }">
            <style>
                .cta-3 {
                    width: 45%;
                }
            </style>
              <div class="subscription-group-title">
                <h4 class="subscription-group-index hide">Profile 1</h4>
                <h4 class="next-order-title body-font-1">Your next order</h4>
                ${
                  items[0].sub.next_charge_scheduled_at
                    ? `<p class="next-order-date title-font-7">${self.convertDateFormat(
                        items[0].sub.next_charge_scheduled_at
                      )}
                    <span class="frequency-badge body-font-3">Every ${
                      items[0].sub.charge_interval_frequency
                    } Month${
                      items[0].sub.charge_interval_frequency > 1 ? 's' : ''
                    }</span>
                  </p>`
                    : `<p class="next-order-date error body-font-2">[Payment Error SVG] There is an issue with your payment method.</p>`
                }
                <div class="subscription-address">
                  <address class="body-font-1">${
                    items[0].subscriptionAddress
                      ? self.formatRechargeAddressObj(
                          items[0].subscriptionAddress
                        )
                      : ''
                  }</address>
                  <button class="button-link cta-link-1 edit-address-btn" data-address-id="${
                    items[0].sub.address_id
                  }">Edit</button>
                </div>
                <div
                    class="subscription-actions actions-top"
                    style="
                        display: flex;
                        flex-wrap: wrap;
                        justify-content: flex-start;
                        align-items: flex-start;"
                >
                  <a class="subscription-action-add-products cta-3" data-profile-id="${profile_id}" id="add-remove-products-btn">Add/Remove products</a>
                  <button class="skip-order-btn cta-3" data-profile-id="${profile_id}" id="adjust-order-date-btn">Adjust Order Date</button>
                  <a class="subscription-action-btn cta-3" data-profile-id="${profile_id}" id="edit-my-subscription-btn">Edit My Subscription</a>
                </div>
                <div class="skip-order-action">
                  <button class="button-link cta-link-1 skip-order-confirmation-btn" data-profile-id="${profile_id}">Skip this order</button>
                </div>
              </div>
              <div class="account-card-list subscriptions" id="${profile_id}">
                ${cardsHTML}
              </div>
              <div class="subscription-actions">
                <button class="cancel-subscription-btn button-link cta-link-1" data-profile-id="${profile_id}">Cancel My Subscription</button>
              </div>
            </div>
          </div>`;

          domFragment.appendChild(wrapper);
        }

        accountBody.appendChild(domFragment);
      });

      requestAnimationFrame(() => {
        self.setupSubscriptionButtons(
          subscriptionGroups,
          reversedSubscriptions
        );
      });

      // setTimeout(() => {
      //   self.setupSubscriptionButtons(
      //     subscriptionGroups,
      //     reversedSubscriptions
      //   );
      // }, 0);

      if (
        document.getElementsByClassName('subscription-profile-list').length ===
        0
      ) {
        throw new Error(
          `No valid / active subscription found under this account`
        );
      }

      requestAnimationFrame(async () => {
        await self.showDiscontinuedTag();
        self.setupAddRemoveEditListeners();
      });
    } catch (e) {
      console.error(e);
      accountBody.innerHTML = '';
      accountBody.insertAdjacentHTML(
        'beforeend',
        document.getElementById('empty-subscription-content').innerHTML
      );
    }

    self.initFrequencyOptions();
  },
  showDiscontinuedTag: async function () {
    const self = this;
    self.removeSubId = [];
    const allProductsCards = document.querySelectorAll(
      '.account-card.subscription'
    );
    Array.from(allProductsCards).forEach((card) => {
      if (card.dataset.externalId === 'null') {
        const cardImage = card.querySelector('.subscription-product-image');
        cardImage.style.display = 'none';
        const discontinuedTag = document.createElement('div');
        discontinuedTag.classList.add('discontinued-tag');
        discontinuedTag.textContent = 'Unavailable';
        card.insertBefore(discontinuedTag, card.firstChild);
        card.setAttribute('data-product-discontinued', 'true');
        self.removeSubId.push(card.dataset.subId);
      }
    });
    self.changeEditSubLinks();
  },

  setupAddRemoveEditListeners: function () {
    const self = this;
    const accountBody = document.querySelector('.account-body');
    const AllSubscriptionsGroup = accountBody.querySelectorAll(
      '.subscription-group'
    );
    Array.from(AllSubscriptionsGroup).forEach((group) => {
      const buttonAddOrRemoveProducts = group.querySelector(
        '#add-remove-products-btn'
      );

      buttonAddOrRemoveProducts.addEventListener('click', (e) => {
        e.preventDefault();
        self.fireGTMEventWithUserData(
          accountGTMEventsMap['account_add_remove_product_btn_clicked']
        );
        window.location.href = buttonAddOrRemoveProducts.href;
      });

      const buttonEditSub = group.querySelector('#edit-my-subscription-btn');

      buttonEditSub.addEventListener('click', (e) => {
        e.preventDefault();
        self.fireGTMEventWithUserData(
          accountGTMEventsMap['account_edit_my_subscription_btn_clicked']
        );
        window.location.href = buttonEditSub.href;
      });
    });
  },

  changeEditSubLinks: async function () {
    const self = this;
    const accountBody = document.querySelector('.account-body');
    const AllSubscriptionsGroup = accountBody.querySelectorAll(
      '.subscription-group'
    );
    // Use Array.from to ensure forEach works properly
    Array.from(AllSubscriptionsGroup).forEach((group) => {
      const buttonAddOrRemoveProducts = group.querySelector(
        '#add-remove-products-btn'
      );
      const buttonEditSub = group.querySelector('#edit-my-subscription-btn');
      if (self.removeSubId.length > 0) {
        if (buttonAddOrRemoveProducts) {
          let currentHref = buttonAddOrRemoveProducts.getAttribute('href');
          self.removeSubId.forEach((id) => {
            currentHref = currentHref.replace(id + ',', '');
            currentHref = currentHref.replace(id, '');
          });
          buttonAddOrRemoveProducts.href = currentHref;
          const urlHasSubscriptions = !currentHref.includes('[]');
          if (!urlHasSubscriptions) {
            buttonAddOrRemoveProducts.classList.add('button-disabled');
          }
        }

        if (buttonEditSub) {
          let currentHref = buttonEditSub.getAttribute('href');
          self.removeSubId.forEach((id) => {
            currentHref = currentHref.replace(id + ',', '');
            currentHref = currentHref.replace(id, '');
          });
          buttonEditSub.href = currentHref;
          const urlHasSubscriptions = !currentHref.includes('[]');
          if (!urlHasSubscriptions) {
            buttonEditSub.classList.add('button-disabled');
          }
        }
      }
    });
  },
  setupSubscriptionButtons(subscriptionGroups, subscriptions) {
    const self = this;
    const editButtons = document.getElementsByClassName(
      'subscription-action-btn'
    );
    const addProductsButtons = document.querySelectorAll(
      '.subscription-action-add-products'
    );

    const skipButtons = document.getElementsByClassName('skip-order-btn');
    const skipConfirmationButtons = document.getElementsByClassName(
      'skip-order-confirmation-btn'
    );
    const cancelButtons = document.getElementsByClassName(
      'cancel-subscription-btn'
    );

    [...cancelButtons].forEach((button) => {
      const profileId = button.dataset.profileId;
      const ids = subscriptionGroups[profileId];
      const chargeDate = subscriptions.find((sub) =>
        ids.includes(sub.id)
      )?.next_charge_scheduled_at;

      const date = new Date(chargeDate + 'T00:00:00Z');
      const now = new Date();
      const diff = date.getTime() - now.getTime();
      const isLessThan24Hours = diff > 0 && diff < TWENTY_FOUR_HOURS_MS;

      if (
        isLessThan24Hours &&
        self?.fobSettings?.enable_cancel_prevention_24_hours
      ) {
        addTooltipOnHover(
          button,
          self?.fobSettings?.text_cancel_prevention_24_hours
        );
        return;
      }

      button.addEventListener('click', (event) => {
        self.fireGTMEventWithUserData(accountGTMEventsMap['cancel_view_offer']);
        self.retentionOfferModal.enabled = true;

        self.cancellation_flow.subscriptionIds = ids;
        self.startCancelFlowBtn.dataset.subscriptionIds = JSON.stringify(ids);
      });
    });

    for (let i = 0; i < editButtons.length; i++) {
      const elements = [
        editButtons[i],
        addProductsButtons[i],
        skipButtons[i],
        skipConfirmationButtons[i],
        cancelButtons[i]
      ];
      const profileId = elements[0].dataset.profileId;
      const subs = subscriptions.filter((s) =>
        subscriptionGroups[profileId].includes(s.id)
      );
      const sub = subs[0];
      const browserNextChargeDate = sub.next_charge_scheduled_at
        ? self.convertDateFormat(sub.next_charge_scheduled_at)
        : false;
      const intervalFrequency = sub.order_interval_frequency;
      const chargeDateExtended = self.addMonthsToDate(
        sub.next_charge_scheduled_at,
        intervalFrequency
      );

      elements[0].href = `${
        self.baseUrl
      }/collections/configuration?editing=1&subscriptions=${JSON.stringify(
        subscriptionGroups[profileId]
      )}`;

      elements[1].href = elements[0].href;

      elements[2].dataset.subscriptionIds = JSON.stringify(
        subscriptionGroups[profileId]
      );
      elements[2].dataset.intervalFrequency = intervalFrequency;
      if (browserNextChargeDate) {
        elements[2].dataset.skipToChargeDate = browserNextChargeDate;
        elements[2].dataset.skipToChargeDateUnformatted =
          sub.next_charge_scheduled_at;
      }

      elements[3].dataset.subscriptionIds = JSON.stringify(
        subscriptionGroups[profileId]
      );
      elements[3].dataset.intervalFrequency = intervalFrequency;
      if (browserNextChargeDate) {
        elements[3].dataset.skipToChargeDate = browserNextChargeDate;
        elements[3].dataset.skipToChargeDateUnformatted =
          sub.next_charge_scheduled_at;
      }
      elements[3].dataset.chargeDateExtended = chargeDateExtended;

      if (sessionStorage.getItem(`skip-order-disabled-profile-${profileId}`)) {
        elements[3].disabled = true;
      }

      elements[4].dataset.subscriptionIds = JSON.stringify(
        subscriptionGroups[profileId]
      );
    }
  },
  initOneTimes: async function () {
    const renderDiv = await OneTimesService.getRenderContent({
      parentView: this
    });

    this.accountBody.insertBefore(renderDiv, this.accountBody.firstChild);
  },

  initPaymentUpdate: async function () {
    const self = this;
    const accountBody = self.accountBody;
    try {
      const dedup_subscriptions = self.deduplicateArrayByProperty(
        self.subscriptions,
        `address_id`
      );

      const { payment_methods } =
        await recharge.paymentMethod.listPaymentMethods(self.session, {
          limit: 10,
          include: 'addresses'
        });
      console.log(payment_methods);

      let paymentInvalidFlag,
        noPaymentFlag = false;

      if (!payment_methods || payment_methods.length == 0) {
        noPaymentFlag = true;
        paymentInvalidFlag = true;
      }

      /*
        Update Payment Flow
      */
      if (!noPaymentFlag) {
        for (const subscribe of dedup_subscriptions) {
          const address_id = subscribe.address_id;
          const customer_id = subscribe.customer_id;

          // Current address's payment method is not valid, do not render update payment flow
          if (!self.paymentMethodValid(address_id, payment_methods)) {
            paymentInvalidFlag = true;
            continue;
          }

          const selectorAsString = `.profile-wrapper[data-address-id="${address_id}"]`;
          console.log(`selectorAsString ===== `, selectorAsString);
          const subscription_profile_list_elms =
            accountBody.querySelectorAll(selectorAsString);

          console.log(
            `subscription_profile_list_elms ===== `,
            subscription_profile_list_elms
          );

          subscription_profile_list_elms.forEach((elm) => {
            console.log(`elm ===== `, elm);
            elm
              .querySelector(`.subscription-actions.actions-top`)
              .insertAdjacentHTML(
                'beforeend',
                `
              <button
                class="update-payment-btn cta-3"
                data-alt-text="Email Sent!"
                data-address-id="${address_id}"
                data-customer-id="${customer_id}"
                style="width: -webkit-fill-available !important">
                Update Payment
              </button>
            `
              );
          });
        }
      }

      /*
        Add Payment Flow
      */
      if (paymentInvalidFlag) {
        // Keep it separate from the UPF flow above, as the add payment flow is depending on Stripe init
        const paymentErrorTemplate = `
          <p class="next-order-date error body-font-2">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="24" height="24" rx="12" fill="#D91022"/>
              <path d="M12 6V13" stroke="#F8FAFA" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12 18C12.5523 18 13 17.5523 13 17C13 16.4477 12.5523 16 12 16C11.4477 16 11 16.4477 11 17C11 17.5523 11.4477 18 12 18Z" fill="#F8FAFA"/>
            </svg>
            There is an issue with your payment method. Click "Add Payment" below to update your information.
          </p>
        `;
        for (const subscribe of dedup_subscriptions) {
          const address_id = subscribe.address_id;
          const customer_id = subscribe.customer_id;

          // Current address's payment method is not valid, render add payment flow
          if (self.paymentMethodValid(address_id, payment_methods)) continue;

          const subscription_profile_list_elms = accountBody.querySelectorAll(
            `.profile-wrapper[data-address-id="${address_id}"]`
          );
          subscription_profile_list_elms.forEach((elm) => {
            // Only show payment error message when address has no payment, for the invalid payment do NOT show the message, but do show Add Payment CTA for both cases
            self.paymentMethodEmpty(address_id, payment_methods) &&
              self.domElementReplace(
                elm.querySelector(`p.next-order-date`),
                paymentErrorTemplate
              );

            elm
              .querySelector(`.subscription-actions.actions-top`)
              .insertAdjacentHTML(
                'beforeend',
                `
              <button class="update-payment-btn cta-3" data-alt-text="Adding ..." data-address-id="${address_id}" data-customer-id="${customer_id}">Add Payment</button>
            `
              );
          });
        }
      }

      if (!noPaymentFlag || paymentInvalidFlag) {
        const confirmPaymentButton = document.getElementById(
          `confirm-update-payment-btn`
        );
        self.paymentUpdateCtl = self.paymentUpdateCtl
          ? self.paymentUpdateCtl
          : OneDrawer.init({
              drawerElem: '#update-payment-modal',
              triggerEvent: 'click',
              triggerElem: '.update-payment-btn',
              openCallback: function (event) {
                const curr = event.target;
                const address_id = curr.dataset.addressId;
                const customer_id = curr.dataset.customerId;
                confirmPaymentButton.disabled = true;

                console.log(`Address id to search is ${address_id}`);
                const found_payment_methods = payment_methods.filter(
                  (method) => {
                    const addresses = method.include?.addresses;
                    const found_address =
                      addresses &&
                      addresses.filter((addr) => addr.id == address_id);
                    return found_address && found_address.length > 0;
                  }
                );

                if (found_payment_methods.length === 0) {
                  const updatePaymentModal = document.querySelector(
                    '#update-payment-modal'
                  );
                  const updatePaymentModalText =
                    updatePaymentModal.querySelector('.payment-note');
                  const updatePaymentButton = updatePaymentModal.querySelector(
                    '#confirm-update-payment-btn'
                  );
                  updatePaymentModalText.innerHTML =
                    "We recently switched to a new payment process and we are unable to update your payment method at this time. To make this right we'd like to offer you a 20% discount code to create a new subscription.";
                  updatePaymentButton.innerHTML = 'Contact Support';
                  updatePaymentButton.removeAttribute('disabled');
                  updatePaymentButton.addEventListener('click', (event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    window.location.href = '/pages/contact-us';
                  });
                }

                if (paymentInvalidFlag) {
                  return;
                }

                let inject_html = '';
                const drawer = document.getElementById('update-payment-modal');

                drawer.querySelector(`.swatches`).innerHTML = '';
                found_payment_methods.forEach((method) => {
                  // Need to validate for express payment and other situation
                  let payment_type_class = self.handleize(method.payment_type),
                    payment_details;
                  switch (method.payment_type) {
                    case 'CREDIT_CARD':
                      payment_type_class = self.handleize(
                        method.payment_details.brand
                      );
                      payment_details = `<span class="title-font-12 card-brand">${method.payment_details.brand} card</span> <span>ends with ****${method.payment_details.last4}</span>`;
                      break;
                    case 'PAYPAL':
                      payment_details = `<span class="title-font-12 card-brand">Paypal</span>`;
                      break;
                    case 'PAYPAL_EXPRESS':
                      payment_details = `<span class="title-font-12 card-brand">Paypal Express</span>`;
                      break;
                    case 'APPLE_PAY':
                      payment_details = `<span class="title-font-12 card-brand">Apple Pay</span>`;
                      break;
                    case 'GOOGLE_PAY':
                      payment_details = `<span class="title-font-12 card-brand">Google Pay</span>`;
                      break;
                    case 'SHOP_PAY':
                      payment_details = `<span class="title-font-12 card-brand">Shopify Shop Pay</span>`;
                      break;
                    default:
                      payment_details = `<span class="title-font-12 card-brand">Other Payment</span>`;
                      break;
                  }

                  inject_html += `
                <div class="swatch-wrapper payment-method-card">
                  <input type="radio" id="${method.id}" name="payment_id" value="${method.id}-${customer_id}-${address_id}">
                  <label for="${method.id}" class="swatch payment-${payment_type_class}">
                    ${payment_details}
                  </label>
                </div>
              `;
                });
                drawer
                  .querySelector(`.swatches`)
                  .insertAdjacentHTML('beforeend', inject_html);

                if (found_payment_methods.length == 1) {
                  console.log(`pre-select the only one payment method`);
                  drawer.querySelector(`.swatches input`).click();
                }
              },
              closeCallback: function () {},
              events: {
                'change .payment-method-list input': function (event) {
                  const value = event.target.value;
                  if (value) {
                    console.log(value);
                    const values = value.split(`-`);
                    confirmPaymentButton.disabled = false;
                    confirmPaymentButton.dataset.pid = values[0];
                    confirmPaymentButton.dataset.cid = values[1];
                    confirmPaymentButton.dataset.aid = values[2];
                  }
                },
                'click #confirm-update-payment-btn': async function (event) {
                  const updatePaymentButton = this.triggerDomElem;
                  const curr = event.target;
                  updatePaymentButton.disabled = true;

                  this.closeDrawer();
                  const result = await self.paymentUpdateRequest(
                    curr.dataset.pid,
                    curr.dataset.cid,
                    curr.dataset.aid
                  );

                  // TODO: need better error handling here!
                  console.log(result);
                  if (!result.success) {
                    alert(result.message);
                    console.error(result.message);
                    updatePaymentButton.disabled = false;
                  } else {
                    const alt_text = updatePaymentButton.dataset.altText;
                    const org_text = updatePaymentButton.innerHTML;
                    updatePaymentButton.innerHTML = alt_text;
                    updatePaymentButton.dataset.altText = org_text;
                  }
                }
              }
            });
      }
    } catch (error) {
      console.error(error.message);
    }
  },

  initSuccessModal: function () {
    const self = this;
    self.successModalCtl = OneDrawer.init({
      drawerElem: '#success-modal',
      isModal: true,
      events: {
        'click #success-modal-subscriptions-btn': async function () {
          this.closeDrawer();
        }
      }
    });
  },

  initAddressModal: function () {
    const self = this;
    AddressModalService.AddFormListeners();

    // Remove the set as default checkbox in the subscription page for now
    document.getElementById('address_default_address').classList.add('hide');
    document
      .getElementById('address_default_address_label')
      .classList.add('hide');

    self.addressModalCtl = OneDrawer.init({
      drawerElem: '#address-modal',
      isModal: true,
      triggerEvent: 'click',
      triggerElem: '.edit-address-btn',
      openCallback: function (event) {
        AddressModalService.ResetAddressForm();
        const addressId = event.target.dataset.addressId;
        const subscriptionAddress = self.addresses.find((address) => {
          return address.id == addressId;
        });
        AddressModalService.PopulateFormWithAddress(subscriptionAddress);
      },
      events: {
        'click #address-submit-btn': async function (event) {
          event.preventDefault();

          let setAsDefault = document.getElementById(
            'address_default_address'
          ).checked;

          if (
            !setAsDefault ||
            (setAsDefault &&
              confirm('Are you sure you want to update your default address?'))
          ) {
            const accountDetailsElem =
              document.getElementById('account-details');
            const shopifyAddresses = JSON.parse(
              accountDetailsElem.dataset.customerAddresses
            );

            const rechargeAddressList = await RechargeGateway.getAddresses();

            AccountService.getUnifiedAddressesFromRechargeAndShopify({
              shopifyAddresses: shopifyAddresses,
              rechargeAddresses: rechargeAddressList
            });

            const buttonElement = event.target;
            const parentForm = buttonElement.parentNode.parentNode;
            const addressId = event.target.getAttribute('data-address-id');

            const addressObject =
              AccountService.getRechargeAddressObjectFromHtmlContainer(
                parentForm
              );

            const checkedObject =
              AccountService.getCheckedRechargeAddressObject(addressObject);

            this.closeDrawer();
            AddressModalService.ShowMainLoader();

            const rechargeResponse = await AccountService.updateRechargeAddress(
              {
                updateAddressObject: checkedObject,
                rechargeAddressId: addressId,
                setAsDefault
              }
            );

            if (rechargeResponse?.error || rechargeResponse?.errorMessage) {
              AddressModalService.HideMainLoader();
              FlashMessageUtil.showFlashMessage({
                message: `There is an error processing your update. Please contact customer service. ${
                  rechargeResponse?.errorMessage
                    ? parseErrorDetailsAsHtml(rechargeResponse.errorMessage)
                    : ''
                }`,
                error: true
              });
              return;
            }

            const unifiedAddr = Object.entries(
              AccountService.addresses.unifiedAddresses
            ).find((address) => {
              return address[1].rechargeAddress.id == addressId;
            });

            if (unifiedAddr) {
              // Update address in Shopify
              const shopifyAddressId = unifiedAddr[0];
              AddressModalService.addressForm.action = `/account/addresses/${shopifyAddressId}`;
              AddressModalService.addressForm.submit();
            } else {
              self.addresses = await RechargeGateway.getAddresses();
              await self.initSubscriptions();
              await self.initOneTimes();
              await self.initPaymentUpdate();
              AddressModalService.HideMainLoader();
              FlashMessageUtil.showFlashMessage({
                message: 'Your address has been updated successfully'
              });
            }
          }
        }
      }
    });

    new Shopify.CountryProvinceSelector(
      // eslint-disable-next-line no-new
      'AddressCountry',
      'AddressProvince',
      {
        hideElement: 'AddressProvinceContainer'
      }
    );
  },

  initSkipOrderConfirmationModal: function () {
    const self = this;
    self.skipOrderModalCtl = OneDrawer.init({
      drawerElem: '#skip-order-confirmation-modal',
      isModal: true,
      triggerEvent: 'click',
      triggerElem: '.skip-order-confirmation-btn',
      openCallback: function (event) {
        const profileId = event.target.getAttribute('data-profile-id');
        const subscriptionIds = event.target.getAttribute(
          'data-subscription-ids'
        );
        const skipChargeDate = event.target.getAttribute(
          'data-skip-to-charge-date'
        );
        const skipChargeDateUnformatted = event.target.getAttribute(
          'data-skip-to-charge-date-unformatted'
        );
        const intervalFrequency = event.target.getAttribute(
          'data-interval-frequency'
        );
        const chargeDateExtended = event.target.getAttribute(
          'data-charge-date-extended'
        );

        const chargeDateElement = document.getElementById(
          'charge-date-extended'
        );
        chargeDateElement.textContent =
          self.convertDateFormat(chargeDateExtended);

        const adjustButton = document.getElementById(
          'skip-order-confirmation-modal-adjust'
        );
        adjustButton.setAttribute('data-subscription-ids', subscriptionIds);
        adjustButton.setAttribute('data-skip-to-charge-date', skipChargeDate);
        adjustButton.setAttribute(
          'data-skip-to-charge-date-unformatted',
          skipChargeDateUnformatted
        );
        adjustButton.setAttribute('data-interval-frequency', intervalFrequency);
        adjustButton.setAttribute('data-previous-modal', '1');
        adjustButton.setAttribute('data-profile-id', profileId);
        chargeDateElement.setAttribute(
          'data-charge-date-extended',
          chargeDateExtended
        );
        const viewSubscriptionButton = document.getElementById(
          'success-modal-subscriptions-btn'
        );
        const saveChanges = document.getElementById(
          'skip-order-confirmation-modal-save'
        );
        viewSubscriptionButton.setAttribute(
          'data-subscription-ids',
          subscriptionIds
        );
        saveChanges.setAttribute('data-profile-id', profileId);
        saveChanges.setAttribute('data-subscription-ids', subscriptionIds);
      },
      events: {
        'click #skip-order-confirmation-modal-adjust': async function () {
          self.fireGTMEventWithUserData(
            accountGTMEventsMap['account_adjust_from_skip_order_modal']
          );
          this.closeDrawer();
          self.skipOrderCtl.openDrawer();
        },
        'click #skip-order-confirmation-modal-save': async function (event) {
          const chargeDateExtended = document
            .getElementById('charge-date-extended')
            .getAttribute('data-charge-date-extended');
          const subscriptionIds = event.target.getAttribute(
            'data-subscription-ids'
          );
          const profileId = event.target.getAttribute('data-profile-id');
          this.closeDrawer();
          const findSubscriptionInterval = () => {
            const subscriptionIdsArray = JSON.parse(subscriptionIds);
            const subscriptions = self.subscriptions;
            const firstSubscriptionId = subscriptionIdsArray[0];
            const subscription = subscriptions.find(
              (sub) => sub.id === firstSubscriptionId
            );
            if (subscription) {
              return subscription.order_interval_frequency;
            }
          };
          const interval = findSubscriptionInterval();
          const result = await self.updateOrderDateAndFrequency(
            subscriptionIds,
            chargeDateExtended,
            interval,
            'month',
            profileId
          );
          self.fireGTMEventWithUserData(
            accountGTMEventsMap['account_save_changes_from_skip_order_modal']
          );
          console.log('result =>', result);
          console.log(result);
          if (!result.success) {
            alert(result.message);
          }
        }
      }
    });
  },

  initAdjustOrderDateAndFrequency: function () {
    const self = this;
    let shipToDate = false;

    self.skipOrderCtl = OneDrawer.init({
      drawerElem: '#skip-order-modal',
      isModal: true,
      triggerEvent: 'click',
      triggerElem: '.skip-order-btn',
      initCallback: function () {
        const currentDate = new Date();
        const futureDate = new Date(currentDate);
        futureDate.setDate(currentDate.getDate() + 180);

        this.datepicker = new AirDatepicker('#change-date-picker', {
          minDate: currentDate,
          maxDate: futureDate,
          locale: localeEn,
          dateFormat: `yyyy-MM-dd`,
          onSelect: ({ date, formattedDate, datepicker }) => {
            console.log(`date picker on select: `);
            console.log(date);
            console.log(formattedDate);

            // const confirmSkipButton = document.getElementById('confirm-skip-order-btn')
            const confirmUpdateButton = document.getElementById(
              'confirm-chanage-order-btn'
            );
            const skipToDateElement =
              document.getElementById('next-shipment-date');

            if (formattedDate) {
              shipToDate = formattedDate;
              // confirmSkipButton.disabled = true;
              confirmUpdateButton.disabled = false;
              skipToDateElement.innerHTML =
                self.convertDateFormat(formattedDate);
            } else {
              shipToDate = false;
              // confirmSkipButton.disabled = false;
              confirmUpdateButton.disabled = true;
              skipToDateElement.innerHTML = skipToDateElement.dataset.skipDate;
            }
          }
        });
      },
      openCallback: function (event) {
        self.fireGTMEventWithUserData(
          accountGTMEventsMap['account_adjust_order_date_btn_clicked']
        );

        const subscriptionIds = event.target.getAttribute(
          'data-subscription-ids'
        );
        const skipChargeDate = event.target.getAttribute(
          'data-skip-to-charge-date'
        );
        const skipChargeDateUnformatted = event.target.getAttribute(
          'data-skip-to-charge-date-unformatted'
        );
        const intervalFrequency = event.target.getAttribute(
          'data-interval-frequency'
        );
        const previousModal = event.target.getAttribute('data-previous-modal');
        const profileId = event.target.getAttribute('data-profile-id');

        // const confirmSkipButton = document.getElementById('confirm-skip-order-btn')
        const confirmUpdateButton = document.getElementById(
          'confirm-chanage-order-btn'
        );
        const skipToDateElement = document.getElementById('next-shipment-date');

        // confirmSkipButton.setAttribute('data-subscription-ids', subscriptionIds)
        confirmUpdateButton.setAttribute(
          'data-subscription-ids',
          subscriptionIds
        );
        if (profileId) {
          confirmUpdateButton.setAttribute('data-profile-id', profileId);
        }
        if (skipChargeDate) {
          skipToDateElement.setAttribute('data-skip-date', skipChargeDate);
        }
        if (skipChargeDateUnformatted) {
          skipToDateElement.setAttribute(
            'data-skip-date-unformatted',
            skipChargeDateUnformatted
          );
        }
        document
          .getElementById('back-button')
          .setAttribute('data-previous-modal', previousModal);

        // confirmSkipButton.disabled = false;
        confirmUpdateButton.disabled = true;
        skipToDateElement.innerHTML = skipChargeDate;

        for (let i = 0; i < self.frequencySelector.options.length; i++) {
          let optionElement = self.frequencySelector.options[i];
          if (optionElement.value == intervalFrequency) {
            optionElement.setAttribute('selected', '');
          } else {
            optionElement.removeAttribute('selected');
          }
        }
      },
      closeCallback: function (event) {
        this.datepicker && this.datepicker.clear();
      },
      events: {
        'click #confirm-chanage-order-btn': async function (event) {
          this.closeDrawer();
          self.accountBody.innerHTML = `<div class="loader" style="background-image: url('${self.accountBody.dataset.loaderImage}');"></div>`;
          const frequencySelected = self.frequencySelector.value;
          const originalNextShipDate =
            document.getElementById('next-shipment-date').dataset
              .skipDateUnformatted;
          const nextShipDateUpdated = shipToDate || originalNextShipDate;
          const profileId = event.target.dataset.profileId;
          const subscriptionIds = event.target.getAttribute(
            'data-subscription-ids'
          );

          if (nextShipDateUpdated != originalNextShipDate) {
            self.fireGTMEventWithUserData(
              accountGTMEventsMap['account_update_next_ship_date']
            );
          }

          const result = await self.updateOrderDateAndFrequency(
            subscriptionIds,
            nextShipDateUpdated,
            frequencySelected,
            'month', // TODO: where should we get this value from?
            profileId
          );

          // TODO: need better error handling here!
          console.log(result);
          if (!result.success) {
            alert(result.message);
          }
        },
        'change #global-frequency-selector': async function (event) {
          const confirmUpdateButton = document.getElementById(
            'confirm-chanage-order-btn'
          );
          const originalNextShipDate =
            document.getElementById('next-shipment-date').dataset
              .skipDateUnformatted;
          if (shipToDate || originalNextShipDate) {
            confirmUpdateButton.disabled = false;
          }
        },
        'click #back-button': async function (event) {
          event.preventDefault();
          self.fireGTMEventWithUserData(
            accountGTMEventsMap[
              'account_back_button_clicked_from_adjust_order_modal'
            ]
          );
          this.closeDrawer();
          if (event.target.dataset.previousModal === '1') {
            self.skipOrderModalCtl.openDrawer();
          }
        }

        // 'click #confirm-skip-order-btn': async function (event) {
        //   this.closeDrawer()
        //   self.accountBody.innerHTML = `<div class="loader" style="background-image: url('${self.accountBody.dataset.loaderImage}');"></div>`
        //   const subscriptionIds = JSON.parse(event.target.dataset.subscriptionIds)
        //   const result = await self.skipOrder(subscriptionIds);

        //   // TODO: need better error handling here!
        //   console.log(result);
        //   if (!result.success) {
        //     alert(result.message);
        //   }
        // },
      }
    });
  },

  initFrequencyOptions: function () {
    const self = this;
    // TODO: This is hardcoded just for now. There might be a way to get the product.selling_plan_groups[0].selling_plans from the subscription
    let frequencyOptions = [
      {
        value: '1',
        text: 'Delivery every 1 Month'
      },
      {
        value: '2',
        text: 'Delivery every 2 Months'
      },
      {
        value: '3',
        text: 'Delivery every 3 Months'
      },
      {
        value: '6',
        text: 'Delivery every 6 Months'
      }
    ];

    let options = '';
    frequencyOptions.forEach((option) => {
      options += `<option value="${option.value}">${option.text}</option>`;
    });
    self.frequencySelector.innerHTML = options;
  },

  checkReasonWithOtherBrands(event, confirm_next_cta) {
    const self = this;
    const existingDropdown = document.getElementById(
      'haircare-brands-dropdown'
    );
    if (existingDropdown) {
      existingDropdown.remove();
    }

    const brandsOnSettings =
      self.cancelationModal.dataset?.otherBrands?.split(',');
    const brands = brandsOnSettings ?? [
      'L’Oreal',
      'Kerastase',
      'Prose',
      'Tresemme',
      'Ouai',
      'OGX',
      'Dove',
      'Pantene',
      'Function of Beauty (Retail)',
      'Head & Shoulders',
      'Olaplex',
      'Curlsmith',
      'Well',
      'Herbal Essences',
      'Other'
    ];

    const dropdownLabel = document.createElement('label');
    dropdownLabel.htmlFor = 'haircare-brands-dropdown';
    dropdownLabel.textContent = 'Select a haircare brand:';
    dropdownLabel.style.background = 'none';

    const dropdown = document.createElement('select');
    dropdown.id = 'haircare-brands-dropdown';
    dropdown.className = 'haircare-brands-dropdown';

    brands.forEach((brand) => {
      const option = document.createElement('option');
      option.value = brand;
      option.textContent = brand;
      dropdown.appendChild(option);
    });

    event.target.parentNode.appendChild(dropdownLabel);
    event.target.parentNode.appendChild(dropdown);
    event.target.parentNode.style.backgroundColor = '#f7fafa';
    // #f7fafa
    const otherBrandInput = document.createElement('input');
    otherBrandInput.type = 'text';
    otherBrandInput.id = 'other-brand-input';
    otherBrandInput.className = 'other-brand-input';
    otherBrandInput.placeholder = 'Please enter the brand name';
    otherBrandInput.style.visibility = 'hidden';
    event.target.parentNode.appendChild(otherBrandInput);

    dropdown.addEventListener('change', (e) => {
      const selected = e.target.value.trim();
      console.log(`Selected: ${selected}`);
      self.cancellation_flow.selectedBrand = e.target.value;

      if (selected.toLowerCase() === 'other') {
        console.log('Other brand selected', otherBrandInput);
        otherBrandInput.style.visibility = 'visible';
        otherBrandInput.value = '';
      } else {
        confirm_next_cta.disabled = false;
        otherBrandInput.style.visibility = 'hidden';
      }
    });
    otherBrandInput.addEventListener('input', (e) => {
      confirm_next_cta.disabled = false;
      self.cancellation_flow.selectedBrand = e.target.value.trim();
    });
  },
  initCancelSubscription: async function () {
    const self = this;

    const modal = document.getElementById('cancel-subscription-modal');

    self.cancelationModal = modal;

    const initial_cancellation_flow = {
      start: false,
      complete: false,
      step: 0,
      subscriptionIds: [],
      reason: '',
      feedback: '',
      delay: '',
      nextShip: false
    };
    const delay_options = [
      {
        value: 1,
        unit_label: 'Month',
        unit_value: '30'
      },
      {
        value: 2,
        unit_label: 'Months',
        unit_value: '30'
      },
      {
        value: 3,
        unit_label: 'Months',
        unit_value: '30'
      }
    ];

    let initial_modal_html = ``;

    self.cancellationReason = ``;

    const closeRetentionModal = () => {
      self.retentionOfferModal.enabled = false;
    };

    const onLoading = () => {
      closeRetentionModal();
      self.showMainLoader();
    };

    const showDiscountSuccess = () => {
      self.alertModal.title = 'Awesome!';
      self.alertModal.description =
        'Your 20% offer has been automatically applied to your next subscription refill';
      self.alertModal.enabled = true;
      self.alertModal.type = 'success';
    };

    const showDiscountError = (message) => {
      self.fireGTMEventWithUserData(accountGTMEventsMap['cancel_offer_error']);
      self.alertModal.title = 'Oops!';
      self.alertModal.description =
        message || "We couldn't apply the discount. Please contact support";
      self.alertModal.enabled = true;
      self.alertModal.type = 'error';
    };

    self.startCancelFlowBtn.addEventListener('click', function () {
      closeRetentionModal();
    });

    self.retentionOfferBtn.addEventListener('click', function () {
      self.fireGTMEventWithUserData(
        accountGTMEventsMap['cancel_accepted_offer']
      );
      self.claimCancellationDiscount(
        onLoading.bind(self),
        self.hideMainLoader.bind(self),
        showDiscountSuccess.bind(self),
        showDiscountError.bind(self)
      );
    });

    self.subscriptionCancelCtl = OneDrawer.init({
      drawerElem: '#cancel-subscription-modal',
      triggerEvent: 'click',
      triggerElem: '#start-cancel-flow-btn',
      initCallback: function () {
        // Init modal and preserve original status
        initial_modal_html = modal.innerHTML;
        self.cancellation_flow = JSON.parse(
          JSON.stringify(initial_cancellation_flow)
        );
      },
      openCallback: function (event) {
        // Reset modal status and start cancellation flow
        self.fireGTMEventWithUserData(accountGTMEventsMap['cancel_start']);
        modal.innerHTML = initial_modal_html;
        self.cancellation_flow = JSON.parse(
          JSON.stringify(initial_cancellation_flow)
        );
        self.cancellationReason = ``;
        self.cancellation_flow.start = true;
        self.cancellation_flow.subscriptionIds = JSON.parse(
          event.target.dataset.subscriptionIds
        );
        self.cancellation_flow.nextShip = event.target.dataset.nextOrderDate;
      },
      closeCallback: function () {
        this.datepicker && this.datepicker.destroy();
        this.datepicker = false;
      },
      events: {
        'click #confirm-cancel-subscription-btn': async function () {
          async function cancelSubscription() {
            const cancelSubscriptionModal = document.querySelector(
              '#cancel-subscription-modal'
            );
            const buttonCloseModal =
              cancelSubscriptionModal.querySelector('.drawer-close');
            buttonCloseModal.click();
            self.accountBody.innerHTML = `<div class="loader" style="background-image: url('${self.accountBody.dataset.loaderImage}');"></div>`;
            const result = await self.cancelSubscription();
            self.cancellation_flow.start = false;
            // TODO: need better error handling here!
            if (!result.success) {
              FlashMessageUtil.showFlashMessage({
                message:
                  "We couldn't cancel your subscription. Please try again later or contact support.",
                error: true
              });
            } else {
              FlashMessageUtil.showFlashMessage({
                message: 'Your subscription was canceled!'
              });

              self.cancellation_flow.complete = true;

              self.cancellation_flow.feedback &&
                self.fireGTMEventWithUserData(
                  accountGTMEventsMap['cancel_send_cs'],
                  {
                    cancellation_feedback:
                      self.cancellation_flow.feedback || 'No Feedback Given'
                  }
                );

              self.cancellation_flow.selectedBrand &&
                self.fireGTMEventWithUserData(
                  accountGTMEventsMap['cancel_switch_brand'],
                  {
                    cancellation_feedback:
                      self.cancellation_flow.feedback || 'No Feedback Given',
                    new_brand: self.cancellation_flow.selectedBrand
                  }
                );

              self.fireGTMEventWithUserData(
                accountGTMEventsMap['cancel_complete'],
                {
                  cancellation_reason:
                    self.cancellation_flow.reason || 'No Reason Given'
                }
              );
            }
          }
          //variable to validate if has shampoo or conditioner with 8 or 16 oz
          let hasShampooOrConditioner = false;
          const subscriptionsToCancel = self.cancellation_flow.subscriptionIds;

          const filteredSubscriptionsSkus = self.subscriptions
            .filter((subscription) =>
              subscriptionsToCancel.includes(subscription.id)
            )
            .map((subscription) => subscription.sku);

          hasShampooOrConditioner = filteredSubscriptionsSkus.some(
            (sku) =>
              (sku?.includes('conditioner') || sku?.includes('shampoo')) &&
              (sku?.includes('8oz') || sku?.includes('16oz'))
          );
          const hasShampooOrConditionerWithCoreReformColor = (element) => {
            const parts = element?.split('.');
            if (parts?.length >= 2) {
              const settings = JSON.parse(localStorage.getItem('fob-settings'));
              const color = parts[2];
              const colorsToSearch = settings.core_reform_colors_remove || '';
              const isCoreReformColor = colorsToSearch
                ?.toLowerCase()
                ?.includes(color?.toLowerCase());
              return isCoreReformColor;
            }
            return false;
          };
          const hasProductWithCoreReformColor = filteredSubscriptionsSkus?.some(
            hasShampooOrConditionerWithCoreReformColor
          );
          if (hasShampooOrConditioner) {
            const settings = JSON.parse(localStorage.getItem('fob-settings'));
            let text = settings.cancel_sub_text ?? '';
            let popupLink = settings.cancel_sub_link_url ?? '';
            let label = settings.cancel_sub_link_label ?? '';
            let container = document.createElement('div');
            container.innerHTML = text;
            let lastParagraph = container.querySelector('p:last-of-type');
            let link = `<a href=${popupLink} class="edit-link" target="_blank"><u><b>${label}</b></u></a>`;

            lastParagraph && (lastParagraph.innerHTML += ' ' + link);

            text = container.innerHTML;
            const cancelSubscriptionModal = document.querySelector(
              '#cancel-subscription-modal'
            );
            cancelSubscriptionModal.querySelector(
              '#cancel-flow-next'
            ).innerHTML = text;
            const modalButtons =
              cancelSubscriptionModal.querySelector('.actions-wrapper');
            modalButtons.innerHTML = `
            <button class="confirm-button cta-3" id="close-modal-button">KEEP MY SUBSCRIPTION</button>
            <button class="confirm-button cta-3" id="cancel-subscription-button">CONTINUE TO CANCEL</button>`;
            const buttonCancelSubscription = modalButtons.querySelector(
              '#cancel-subscription-button'
            );
            buttonCancelSubscription.addEventListener('click', function () {
              cancelSubscription().then();
            });
          } else if (hasProductWithCoreReformColor) {
            RegimenUtils.changeCancelModalForCoreReformColors(
              cancelSubscription
            );
          } else {
            await cancelSubscription();
          }
        },
        'click #cancellation-claim-discount-btn': function () {
          const onFinally = this.closeDrawer.bind(this);
          self.claimCancellationDiscount(null, onFinally, null, null);
        },
        'click #close-modal-button': async function () {
          this.closeDrawer();
        },
        'click #delay-subscription-btn': async function () {
          this.closeDrawer();
          self.accountBody.innerHTML = `<div class="loader" style="background-image: url('${self.accountBody.dataset.loaderImage}');"></div>`;
          const result = await self.updateOrderDateAndFrequency(
            self.cancellation_flow.subscriptionIds,
            self.cancellation_flow.delay
          );
          self.cancellation_flow.start = false;

          // TODO: need better error handling here!
          console.log(result);
          if (!result.success) {
            alert(result.message);
          } else {
            self.fireGTMEventWithUserData(
              accountGTMEventsMap['cancel_update_date']
            );
          }
        },
        'click #confirm-cancel-next-btn': function (event) {
          if (self.cancellationReason) {
            const modal = this;
            const step_0 = document.getElementById('cancel-flow-reason');
            const step_1 = document.getElementById('cancel-flow-next');
            const selected_reason_elm = document.getElementById(
              `strategy-${self.cancellationReason}`
            );
            const selected_reason_text = document.getElementById(
              `strategy-${self.cancellationReason}-text`
            )?.textContent;
            const confirm_next_cta = document.getElementById(
              'confirm-cancel-next-btn'
            );
            const confirm_cancel_cta = document.getElementById(
              'confirm-cancel-subscription-btn'
            );
            const confirm_change_cta = document.getElementById(
              'delay-subscription-btn'
            );
            self.cancellation_flow.reason = selected_reason_text
              ?.replace(/[\n\r]+|[\s]{2,}/g, ' ')
              ?.trim();

            const buildNextDefault = function (next_step_html) {
              next_step_html += `<textarea placeholder="Tell us more about why you're canceling" class="" id="cancellation_feedback" maxLength="1024"></textarea>`;
              confirm_cancel_cta.classList.add('cta-1');
              confirm_cancel_cta.classList.remove('hide', 'hidden', 'cta-3');
              return next_step_html;
            };

            const buildClaimDiscount = function (next_step_html) {
              next_step_html += `<button class="confirm-button cta-3" id="cancellation-claim-discount-btn">Claim my 15% discount</button>`;
              confirm_cancel_cta.disabled = false;
              confirm_cancel_cta.classList.add('cta-1');
              confirm_cancel_cta.classList.remove('hide', 'hidden', 'cta-3');
              return next_step_html;
            };

            const buildNextChangeDate = function (next_step_html) {
              const currentDate = new Date();
              const futureDate = new Date(currentDate);
              const skipToDateElement = document.getElementById(
                'cancel-next-shipment-date'
              );

              futureDate.setDate(currentDate.getDate() + 30);
              skipToDateElement.innerHTML = self.convertDateFormat(
                self.cancellation_flow.nextShip
              );

              modal.datepicker = new AirDatepicker(
                '#cancel-change-date-picker',
                {
                  minDate: currentDate,
                  maxDate: futureDate,
                  locale: localeEn,
                  dateFormat: `yyyy-MM-dd`,
                  onSelect: ({ date, formattedDate, datepicker }) => {
                    console.log(`date picker on select: `);
                    console.log(date);
                    console.log(formattedDate);
                    if (formattedDate) {
                      self.cancellation_flow.delay = formattedDate;
                      skipToDateElement.innerHTML =
                        self.convertDateFormat(formattedDate);
                      confirm_change_cta.disabled = false;
                    } else {
                      self.cancellation_flow.delay = false;
                      confirm_change_cta.disabled = true;
                      skipToDateElement.innerHTML = self.convertDateFormat(
                        self.cancellation_flow.nextShip
                      );
                    }
                  }
                }
              );

              confirm_change_cta.classList.remove('hide', 'hidden');
              confirm_cancel_cta.classList.remove('hide', 'hidden');
              confirm_cancel_cta.parentElement.classList.add('multi');
              confirm_cancel_cta.disabled = false;
              return '';
            };

            let next_step_html = `<label for="cancellation_feedback" class="flow-title">${selected_reason_elm?.dataset.prevention}</label>`;
            switch (selected_reason_elm?.dataset.action) {
              case `delay_subscription`:
                next_step_html = buildNextChangeDate(next_step_html);
                break;
              case `delay_subscription`:
                next_step_html = buildNextChangeDate(next_step_html);
                break;
              case `apply_discount`:
                next_step_html = buildClaimDiscount(next_step_html);
                break;
              default:
                next_step_html = buildNextDefault(next_step_html);
                break;
            }

            // Move to next step
            step_0.classList.add('hide', 'hidden');
            if (next_step_html) {
              step_1.innerHTML = next_step_html;
            }
            step_1.classList.remove('hide', 'hidden');
            self.cancellation_flow.step = 1;
            confirm_next_cta.classList.add('hide', 'hidden');
          }
        },
        'change #cancellation_reason input': function (event) {
          const curr = event.target;
          const confirm_next_cta = document.getElementById(
            'confirm-cancel-next-btn'
          );
          const isChangingBrandsReason =
            curr.dataset?.description?.includes('brands');
          console.log(
            `Reason selected: ${curr.value}`,
            isChangingBrandsReason,
            curr.dataset
          );
          self.cancellationReason = curr.value;

          if (!isChangingBrandsReason) {
            confirm_next_cta.disabled = false;
            return;
          }
          if (isChangingBrandsReason) {
            self.checkReasonWithOtherBrands(event, confirm_next_cta);
            return;
          }
        },
        'input #cancellation_feedback': function (event) {
          const feedback = event.target.value ? event.target.value : '';
          if (feedback) {
            const confirm_cancel_cta = document.getElementById(
              'confirm-cancel-subscription-btn'
            );
            confirm_cancel_cta.disabled = false;
          }
          self.cancellation_flow.feedback = feedback;
        }
      }
    });
  },

  claimCancellationDiscount: async function (
    onLoading = null,
    onFinally = null,
    onSuccess = null,
    onError = null
  ) {
    const self = this;
    const subToSend = self.cancellation_flow.subscriptionIds[0];
    let message = '';
    if (typeof onLoading === 'function') {
      onLoading(message);
    }
    try {
      await AwsCloudGateway.applyDiscount(subToSend);
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
    } catch (error) {
      const errorMessage = error.response ? error.response.data : error.message;
      message = errorMessage?.error?.error;
      if (typeof onError === 'function') {
        onError(message);
      }
    } finally {
      if (typeof onFinally === 'function') {
        onFinally();
      }
    }
  },

  createApplyDiscountTicket: async function (message) {
    const self = this;
    const description = `The user ${
      self.session.customerId
    } has requested a cancellation claim discount for the following subscriptions: ${self.cancellation_flow.subscriptionIds.join(
      ', '
    )} and got the following message from the website: ${message}`;
    const email = window.marketingUserData.email;
    const type = 'Claim Discount';
    const name = window.marketingUserData.name;
    const priority = 1;
    const subject = 'Cancellation Claim Discount';

    const settings = JSON.parse(localStorage.getItem('fob-settings'));
    const domain = settings.freshdesk_domain ?? '';
    const apiKeyPlusPass = process.env.API_FRESHDESK_KEY_PLUS_PASS;

    new FreshDeskService({ apiKeyPlusPass, domain }).sendTicket({
      description,
      email,
      type,
      name,
      priority,
      subject,
      onSuccess: () => {
        console.log('Ticket created successfully!');
      },
      onError: () => {
        console.log('It was not possible to create the ticket');
      },
      onFinally: () => {}
    });
  },

  updateOrderDateAndFrequency: async function (
    sub_ids,
    shipToDate,
    frequency,
    intervalUnit,
    profileId
  ) {
    const firstSubscriptionId = JSON.parse(sub_ids)[0];
    const self = this;
    const subscriptionIds = JSON.parse(sub_ids);
    const result = {
      success: false,
      message: ''
    };

    try {
      console.log(`[Change/Skip] subscription ids:`);
      console.log(subscriptionIds);

      if (
        !subscriptionIds ||
        (subscriptionIds && subscriptionIds.length == 0)
      ) {
        throw new Error(`No subscription found.`);
      }

      if (!shipToDate && !frequency) {
        throw new Error(`No data set.`);
      }

      console.log(`need to update the next charge date to ${shipToDate}`);

      self.showMainLoader();

      if (frequency || shipToDate) {
        const arraySubscriptions = [];
        const foundSubscription = self.subscriptions.filter(
          (element) => element.id == firstSubscriptionId
        );
        const addressId = foundSubscription[0].address_id;
        for (const subscription_id of subscriptionIds) {
          const data = {
            ...(frequency && {
              charge_interval_frequency: frequency,
              order_interval_frequency: frequency
            }),
            ...(intervalUnit && { order_interval_unit: intervalUnit }),
            ...(shipToDate && { next_charge_scheduled_at: shipToDate }),
            ...(addressId && { address_id: addressId })
          };
          arraySubscriptions.push({
            id: subscription_id,
            data
          });
        }

        try {
          await AwsCloudGateway.cancelUpdateAndCreateSubscription(
            arraySubscriptions,
            [],
            []
          );
        } catch (error) {
          console.error('error on update');
        }
      }

      if (profileId) {
        sessionStorage.setItem(`skip-order-disabled-profile-${profileId}`, '1');
      }
      result.success = true;
    } catch (error) {
      console.error(error);
      result.message = error.message;
    } finally {
      await self.initSubscriptions();
      self.hideMainLoader();
      self.displaySuccessModal(shipToDate);
      await self.initOneTimes();
      await self.initPaymentUpdate();
    }

    return result;
  },

  skipOrder: async function (sub_ids) {
    const self = this;
    const subscriptionIds = sub_ids;
    const result = {
      success: false,
      message: ''
    };

    try {
      console.log(`[Change/Skip] subscription ids:`);
      console.log(subscriptionIds);

      if (
        !subscriptionIds ||
        (subscriptionIds && subscriptionIds.length == 0)
      ) {
        throw new Error(`No subscription found.`);
      }

      // TODO Move this to the drawer opening so we can use the next delivery list to generate the 'Skip to Date'
      const deliveryList = await recharge.customer.getDeliverySchedule(
        self.session
      );
      console.log(`[Change/Skip] delivery list:`);
      console.log(deliveryList);

      let foundOrderIndex;
      const nextDeliveries = deliveryList?.filter((delivery) => {
        return delivery.orders.some((order, index) => {
          const founded = order.line_items.some((lineItem) => {
            return (
              subscriptionIds.includes(lineItem.subscription_id) &&
              !lineItem.is_skipped &&
              lineItem.is_skippable
            );
          });
          if (founded) {
            foundOrderIndex = index;
            return true;
          }
        });
      });

      console.log(`[Change/Skip] next deliveries:`);
      console.log(nextDeliveries);

      if (!nextDeliveries || (nextDeliveries && nextDeliveries.length == 0)) {
        throw new Error(
          `No next delivery found, please contact customer service.`
        );
      } else {
        const nextDelivery = nextDeliveries[0];
        const nextDate = nextDelivery.date;
        const order = nextDelivery.orders[foundOrderIndex];

        console.log(`[Change/Skip] order matched in next delivery:`);
        console.log(order);

        if (order.charge_id) {
          console.log(
            `[ReCharge API] call skipCharge to skip a exist charge on the order`
          );
          await recharge.charge.skipCharge(
            self.session,
            order.charge_id,
            subscriptionIds
          );
        } else {
          console.log(
            `[ReCharge API] call skipSubscriptionCharge to skip the subscription without exist charge on the order`
          );
          for (const subscription_id of subscriptionIds) {
            await recharge.subscription.skipSubscriptionCharge(
              self.session,
              subscription_id,
              nextDate
            );
          }
        }

        result.success = true;
      }
    } catch (error) {
      console.error(error);
      result.message = error.message;
    }

    await self.initSubscriptions();
    await self.initOneTimes();
    await self.initPaymentUpdate();

    return result;
  },

  cancelSubscription: async function () {
    const self = this;
    const subscriptionIds = self.cancellation_flow.start
      ? self.cancellation_flow.subscriptionIds
      : [];

    let cancellation_reason = ``;
    if (self.cancellation_flow.reason && self.cancellation_flow.reason != '') {
      cancellation_reason += `${self.cancellation_flow.reason}`;
    }

    const result = {
      success: false,
      message: ''
    };

    try {
      console.log(`[Cancel] subscription ids:`);
      console.log(subscriptionIds);

      if (
        !subscriptionIds ||
        (subscriptionIds && subscriptionIds.length == 0)
      ) {
        throw new Error(`No subscription found.`);
      }

      const substToCancel = subscriptionIds.map((item) => ({
        id: item,
        reason: cancellation_reason.substring(0, 1024),
        comment: self.cancellation_flow.feedback ?? ''
      }));

      let response;
      if (substToCancel.length >= 1) {
        response = await AwsCloudGateway.cancelUpdateAndCreateSubscription(
          [],
          substToCancel,
          []
        );
      }

      result.success = !!response;
    } catch (error) {
      console.error(error);
      result.message = error.message;
    }

    await self.initSubscriptions();
    await self.initOneTimes();
    await self.initPaymentUpdate();

    return result;
  },

  paymentUpdateRequest: async function (pid, cid, aid = '') {
    const self = this;
    const result = {
      success: false,
      message: ''
    };

    const payment_id = pid;
    const customer_id = cid;
    const address_id = aid;

    try {
      /* Using SDK method (void) */
      if (!payment_id || !address_id) {
        throw new Error(`No payment or address info, cannot update payment`);
      }

      await recharge.customer.sendCustomerNotification(
        self.session,
        'SHOPIFY_UPDATE_PAYMENT_INFO',
        {
          address_id: address_id,
          payment_method_id: payment_id
        }
      );

      /* Using Admin method (response) */
      // if (!payment_id || !customer_id) {
      //   throw(new Error(`No payment or customer info, cannot update payment`))
      // }

      // const response = await fetch(`
      //   ${window.Shopify.routes.root}apps/update_payment?payment_id=${payment_id}&customer_id=${customer_id}
      // `);
      // const responseData = await response.json();
      // console.log(responseData);

      // if (!responseData || !responseData.success) {
      //   throw(new Error(`${responseData.message}`))
      // }

      self.fireGTMEventWithUserData(
        accountGTMEventsMap['account_update_payment_request']
      );

      result.success = true;
      result.message = `You will receive an email to set the new payment method!`;
    } catch (error) {
      console.error(error);
      if (self.isJSON(error.message)) {
        const message_obj = JSON.parse(error.message);
        result.message = message_obj.error.toString();
      } else {
        result.message = error.message;
      }
    }

    return result;
  },

  paymentMethodValid: function (address_id, payment_methods) {
    // A valid payment: must be attached to the given address, must be a ReCharge compatible payment
    const foundPayment = payment_methods.filter((payment) => {
      if (!payment.include.addresses) return false;
      const foundAddress = payment.include.addresses.filter(
        (address) => address.id == address_id
      );
      return (
        foundAddress &&
        foundAddress.length &&
        payment.processor_name != `braintree`
      );
      // return foundAddress && foundAddress.length && payment.processor_name == `shopify_payments`
    });
    return foundPayment && foundPayment.length;
  },

  paymentMethodEmpty: function (address_id, payment_methods) {
    const foundPayment = payment_methods.filter((payment) => {
      if (!payment.include.addresses) return false;
      const foundAddress = payment.include.addresses.filter(
        (address) => address.id == address_id
      );
      return foundAddress && foundAddress.length;
    });
    return !foundPayment || !foundPayment.length;
  },

  searchProductById: async function (pid) {
    try {
      const response = await getRequest(
        ` ${window.Shopify.routes.root}search/suggest.json?type=product&q=id:${pid}&resources[type]=product&resources[limit]=1`
      );
      const responseData = await response.data;
      // console.log(responseData);
      return responseData.resources.results.products[0];
    } catch (error) {
      console.log(error);
      return false;
    }
  },

  getProductByhandle: async function (handle) {
    try {
      const response = await getRequest(
        `${window.Shopify.routes.root}products/${handle}.js`
      );
      const responseData = await response.data;
      // console.log(responseData);
      return responseData;
    } catch (error) {
      console.log(error);
      return false;
    }
  },

  getSelectedVaraintImage: async function (handle, pid, vid) {
    const self = this;
    let productData = false,
      productImage;
    if (handle) {
      productData = await self.getProductByhandle(handle);
      productImage = `${productData?.featured_image ?? ''}&amp;width=200`;
    } else {
      const productSearchResult = await self.searchProductById(pid);
      const retrieved_handle = productSearchResult
        ? productSearchResult.handle
        : false;
      productImage = productSearchResult
        ? productSearchResult.image + '&amp;width=200'
        : false;
      productData = await self.getProductByhandle(retrieved_handle);
    }

    if (!productData) {
      return productImage;
    }

    try {
      const foundVariant = productData.variants.filter(
        (variant) => variant.id == vid
      );
      const variantImage = foundVariant[0].featured_image;
      if (!variantImage) {
        throw new Error(`variant image not set, use product image as fallback`);
      }
      return variantImage.src + '&amp;width=200';
    } catch (error) {
      console.log(error.message);
      return productImage;
    }
  },

  getProductInfo: async function (pid, vid) {
    const product = await ProductsService.getProduct(
      `gid://shopify/Product/${pid}`
    );
    if (!product) {
      return {
        id: null,
        handle: null,
        image: null
      };
    }

    const variantNode = vid
      ? product.variants?.edges.find(
          (edge) => edge.node.id === `gid://shopify/ProductVariant/${vid}`
        )?.node
      : null;

    const imageUrl =
      variantNode?.image?.url || product.featuredImage?.url || null;

    return {
      id: product.id,
      handle: product.handle,
      image: imageUrl
    };
  },

  valiateProductImage: async function (url, handle, pid, vid) {
    const self = this;
    let productImageUrl = url;

    /* New methods: if url is not supplied, use handle for instead, also check if url is a handle if so use it */
    if (!url) {
      // Image url is not exist, get product image from Shopify
      productImageUrl = await self.getSelectedVaraintImage(handle, pid, vid);
    } else if (self.isShopifyProductHandle(url)) {
      // The supplied image url is actually a product handle, use it as handle.
      productImageUrl = await self.getSelectedVaraintImage(url, pid, vid);
    } else {
      // Image url is supplied in cdn url format, check do noraml validation
      const isImageExist = await self.checkIfImageExists(url);
      // const isImageExist = true; // Skip image check due to slow response of .gif files
      if (!isImageExist) {
        console.log(
          `image is not exist, need to load image from shopify for ${pid}`
        );
        productImageUrl = await self.getSelectedVaraintImage(handle, pid, vid);
      } else {
        console.log(`image is available, use it for ${pid}`);
      }
    }

    return productImageUrl;
  },

  checkIfImageExists: async function (url) {
    return new Promise(async (resolve, reject) => {
      const img = new Image();
      img.src = url;
      if (img.complete) {
        resolve(true);
      } else {
        img.onload = () => {
          resolve(true);
        };
        img.onerror = () => {
          resolve(false);
        };
      }
    });
  },

  /*
    Utility Functions
  */
  parseRechargeDate: function (date) {
    if (date) {
      const parts = date.split('-');
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      return new Date(year, month, day);
    } else {
      return new Date();
    }
  },

  convertDateFormat: function (date_str) {
    const date = this.parseRechargeDate(date_str);
    return date.toLocaleDateString('en-us', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  },

  addDaysToDate: function (inputDate, daysToAdd) {
    if (!inputDate || inputDate == '') {
      return false;
    }

    // Parse the inputDate string into a Date object
    const dateParts = inputDate.split('-');
    const year = parseInt(dateParts[0]);
    const month = parseInt(dateParts[1]) - 1; // JavaScript months are 0-based
    const day = parseInt(dateParts[2]);
    const originalDate = new Date(year, month, day);

    // Calculate the new date by adding daysToAdd
    const newDate = new Date(originalDate);
    newDate.setDate(originalDate.getDate() + parseInt(daysToAdd));

    // Format the new date as "yyyy-mm-dd"
    const newYear = newDate.getFullYear();
    const newMonth = String(newDate.getMonth() + 1).padStart(2, '0'); // Ensure two digits for month
    const newDay = String(newDate.getDate()).padStart(2, '0'); // Ensure two digits for day
    const newFormattedDate = `${newYear}-${newMonth}-${newDay}`;

    return newFormattedDate;
  },

  addMonthsToDate: function (inputDate, monthsToAdd) {
    if (!inputDate || inputDate == '') {
      return false;
    }

    // Parse the inputDate string into a Date object
    const dateParts = inputDate.split('-');
    const year = parseInt(dateParts[0]);
    const month = parseInt(dateParts[1]) - 1; // JavaScript months are 0-based
    const day = parseInt(dateParts[2]);
    const originalDate = new Date(year, month, day);

    originalDate.setMonth(originalDate.getMonth() + monthsToAdd);

    // Format the new date as "yyyy-mm-dd"
    const newYear = originalDate.getFullYear();
    const newMonth = (originalDate.getMonth() + 1).toString().padStart(2, '0'); // Ensure two digits for month
    const newDay = originalDate.getDate().toString().padStart(2, '0'); // Ensure two digits for day
    const extendedDateFormatted = `${newYear}-${newMonth}-${newDay}`;

    return extendedDateFormatted;
  },

  deduplicateArrayByProperty: function (arr, property) {
    const uniqueMap = new Map();

    for (const obj of arr) {
      if (!uniqueMap.has(obj[property])) {
        uniqueMap.set(obj[property], obj);
      }
    }

    return [...uniqueMap.values()];
  },

  isShopifyProductHandle: function (inputString) {
    // Regular expression to match Shopify product handles
    const regex = /^[a-z0-9-]+$/;
    // Use the test method to check if the inputString matches the regex pattern
    return regex.test(inputString);
  },

  handleize: function (title) {
    // Convert the title to lowercase
    const lowercaseTitle = title.toLowerCase();

    // Replace spaces with hyphens
    const handle = lowercaseTitle.replace(/\s+/g, '-');

    // Remove special characters and non-alphanumeric characters
    const cleanHandle = handle.replace(/[^a-z0-9-]/g, '');

    return cleanHandle;
  },

  isJSON: function (string_to_test) {
    try {
      JSON.parse(string_to_test);
    } catch (error) {
      return false;
    }
    return true;
  },

  stall: async function (milliseconds) {
    return new Promise((resolve) => {
      setTimeout(resolve, milliseconds);
    });
  },

  domElementReplace: function (oldElm, newElmTemp) {
    if (oldElm.outerHTML) {
      oldElm.outerHTML = newElmTemp;
    } else {
      const tmpObj = document.createElement('div');
      tmpObj.innerHTML = `<!--THIS DATA SHOULD BE REPLACED-->`;

      const ObjParent = oldElm.parentNode;
      ObjParent.replaceChild(tmpObj, oldElm);
      ObjParent.innerHTML = ObjParent.innerHTML.replace(
        `<div><!--THIS DATA SHOULD BE REPLACED--></div>`,
        newElmTemp
      );
    }
    return oldElm;
  },

  destroy: function () {
    this.undelegateEvents();
  },

  displaySuccessModal: function (shipToDate) {
    const self = this;
    document.getElementById('success-modal-next-order-date').textContent =
      self.convertDateFormat(shipToDate);
    self.successModalCtl.openDrawer();
  },

  showMainLoader: function () {
    const self = this;
    self.accountBody.style.display = 'none';
    self.mainLoader.style.display = 'block';
    document.documentElement.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  },

  hideMainLoader: function () {
    const self = this;
    self.accountBody.style.display = 'block';
    self.mainLoader.style.display = 'none';
  },

  formatRechargeAddressObj: function (addressObj) {
    return `${addressObj.address1}${
      addressObj.address2 ? ' ' + addressObj.address2 : ''
    }, ${addressObj.city}, ${addressObj.country_code}`;
  },

  populateFormWithAddress: function (addressObj) {
    const formFields = {
      first_name: document.getElementById('AddressFirstName'),
      last_name: document.getElementById('AddressLastName'),
      company: document.getElementById('AddressCompany'),
      address1: document.getElementById('AddressAddress1'),
      address2: document.getElementById('AddressAddress2'),
      city: document.getElementById('AddressCity'),
      zip: document.getElementById('AddressZip'),
      phone: document.getElementById('AddressPhone')
    };

    for (const [key, field] of Object.entries(formFields)) {
      field.value = addressObj[key] || '';
    }

    let country = document.getElementById('AddressCountry');
    country.value = AccountCountryNames[addressObj?.country_code];
    let event = new Event('change', { bubbles: true });
    country.dispatchEvent(event);
    let province = document.getElementById('AddressProvince');
    province.value = addressObj?.province;

    let submitButton = document.getElementById('address-submit-btn');
    submitButton.dataset.addressId = addressObj.id;
  }
});
